import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Button,
  Alert,
  TextField,
  Stack,
  Skeleton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useMaterialReactTable } from "material-react-table";
import DataTable from "../../ui/data/DataTable";
import PageContainer from "../../ui/layout/PageContainer";
import BarChartComponent from "../../components/barChart/BarChartComponent";
import PieChartComponent from "../../components/barChart/PieChartComponent";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Cell,
} from "recharts";
import { getRequestWithToken, patchRequestWithToken } from "../../utils/ApiRequests";
import ModalComponent from "../../components/ModalComponent/ModalComponent";
import "./snf.scss";

// CustomTabPanel component for tab content
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`snf-tabpanel-${index}`}
      aria-labelledby={`snf-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `snf-tab-${index}`,
    "aria-controls": `snf-tabpanel-${index}`,
  };
}

function CategoryTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const { label, value } = payload[0].payload;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 6,
        padding: "8px 12px",
        maxWidth: 260,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        wordBreak: "break-word",
        whiteSpace: "normal",
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12, color: "#555" }}>{Number(value).toLocaleString()} candidates</div>
    </div>
  );
}

function CustomYAxisTick({ x, y, payload }) {
  const label = payload?.value || "";
  const maxCharsPerLine = 20;
  const lines = [];
  let currentLine = "";

  // Split on spaces first; for space-less long words (e.g. hyphenated slugs) also split on hyphens
  const words = label.split(" ");
  for (const word of words) {
    if (word.length > maxCharsPerLine) {
      // Flush accumulated line before handling the long word
      if (currentLine) { lines.push(currentLine); currentLine = ""; }
      // Break on hyphens, keeping the hyphen at the end of each segment
      const parts = word.split("-");
      let chunk = "";
      parts.forEach((part, i) => {
        const seg = i < parts.length - 1 ? `${part}-` : part;
        const test = chunk ? `${chunk}${seg}` : seg;
        if (test.length > maxCharsPerLine && chunk) {
          lines.push(chunk);
          chunk = seg;
        } else {
          chunk = test;
        }
      });
      if (chunk) currentLine = chunk;
    } else {
      const test = currentLine ? `${currentLine} ${word}` : word;
      if (test.length > maxCharsPerLine && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = test;
      }
    }
  }
  if (currentLine) lines.push(currentLine);

  const lineHeight = 14;
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, i) => (
        <text
          key={i}
          x={-6}
          y={(i - (lines.length - 1) / 2) * lineHeight}
          textAnchor="end"
          fill="#555"
          fontSize={11}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

const SnfDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(() => {
    const t = parseInt(searchParams.get("tab"), 10);
    return t >= 0 && t <= 4 ? t : 0;
  });
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [abandonedCartStatuses, setAbandonedCartStatuses] = useState({});
  const [apiSummaryData, setApiSummaryData] = useState(null);
  const [summaryError, setSummaryError] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [startDate, setStartDate] = useState("2026-01-01");
  const [endDate, setEndDate] = useState("");

  // Charts state
  const [revenuePeriod, setRevenuePeriod] = useState("");
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [revenueChartLoading, setRevenueChartLoading] = useState(false);
  const [revenueChartError, setRevenueChartError] = useState("");
  const [categoryChartData, setCategoryChartData] = useState([]);
  const [categoryChartLoading, setCategoryChartLoading] = useState(false);
  const [categoryChartError, setCategoryChartError] = useState("");

  // Employers tab state
  const [employersData, setEmployersData] = useState([]);
  const [employersLoading, setEmployersLoading] = useState(false);
  const [employersError, setEmployersError] = useState("");
  const [employersPagination, setEmployersPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [employersPage, setEmployersPage] = useState(1);
  const [employersPageSize, setEmployersPageSize] = useState(20);
  const [employersSearch, setEmployersSearch] = useState("");
  const [employersSearchInput, setEmployersSearchInput] = useState("");
  const [employersSortBy, setEmployersSortBy] = useState("lastSessionAt");
  const [employersOrder, setEmployersOrder] = useState("desc");
  const [employersStartDate, setEmployersStartDate] = useState("2026-01-01");
  const [employersEndDate, setEmployersEndDate] = useState("");
  const [employersStartDateInput, setEmployersStartDateInput] = useState("2026-01-01");
  const [employersEndDateInput, setEmployersEndDateInput] = useState("");
  const [employersFilterError, setEmployersFilterError] = useState("");

  // City-searches tab state
  const [citySearchesData, setCitySearchesData] = useState([]);
  const [citySearchesLoading, setCitySearchesLoading] = useState(false);
  const [citySearchesError, setCitySearchesError] = useState("");
  const [citySearchesPagination, setCitySearchesPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [citySearchesPage, setCitySearchesPage] = useState(1);
  const [citySearchesPageSize, setCitySearchesPageSize] = useState(20);
  const [citySearchesCityInput, setCitySearchesCityInput] = useState("");
  const [citySearchesStateInput, setCitySearchesStateInput] = useState("");
  const [citySearchesStartDateInput, setCitySearchesStartDateInput] = useState("2026-01-01");
  const [citySearchesEndDateInput, setCitySearchesEndDateInput] = useState("");
  const [citySearchesCityFilter, setCitySearchesCityFilter] = useState("");
  const [citySearchesStateFilter, setCitySearchesStateFilter] = useState("");
  const [citySearchesStartDate, setCitySearchesStartDate] = useState("2026-01-01");
  const [citySearchesEndDate, setCitySearchesEndDate] = useState("");
  const [citySearchesFilterError, setCitySearchesFilterError] = useState("");

  // Purchased data tab state
  const [purchasedData, setPurchasedData] = useState([]);
  const [purchasedLoading, setPurchasedLoading] = useState(false);
  const [purchasedError, setPurchasedError] = useState("");
  const [purchasedPagination, setPurchasedPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [purchasedPage, setPurchasedPage] = useState(1);
  const [purchasedPageSize, setPurchasedPageSize] = useState(20);
  const [purchasedCityInput, setPurchasedCityInput] = useState("");
  const [purchasedStartDateInput, setPurchasedStartDateInput] = useState("2026-01-01");
  const [purchasedEndDateInput, setPurchasedEndDateInput] = useState("");
  const [purchasedCityFilter, setPurchasedCityFilter] = useState("");
  const [purchasedStartDate, setPurchasedStartDate] = useState("2026-01-01");
  const [purchasedEndDate, setPurchasedEndDate] = useState("");
  const [purchasedFilterError, setPurchasedFilterError] = useState("");

  // Abandoned cart tab state
  const [abandonedCartApiData, setAbandonedCartApiData] = useState([]);
  const [abandonedCartLoading, setAbandonedCartLoading] = useState(false);
  const [abandonedCartError, setAbandonedCartError] = useState("");
  const [abandonedCartPagination, setAbandonedCartPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [abandonedCartPage, setAbandonedCartPage] = useState(1);
  const [abandonedCartPageSize, setAbandonedCartPageSize] = useState(20);
  const [abandonedCartStartDateInput, setAbandonedCartStartDateInput] = useState("2026-01-01");
  const [abandonedCartEndDateInput, setAbandonedCartEndDateInput] = useState("");
  const [abandonedCartStartDate, setAbandonedCartStartDate] = useState("2026-01-01");
  const [purchasedDemandFulfilled, setPurchasedDemandFulfilled] = useState(null);
  const [abandonedCartEndDate, setAbandonedCartEndDate] = useState("");
  const [abandonedCartFilterError, setAbandonedCartFilterError] = useState("");
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [isTesterFilter, setIsTesterFilter] = useState(false);

  // Purchased candidates modal state
  const [candidatesModalOpen, setCandidatesModalOpen] = useState(false);
  const [candidatesModalData, setCandidatesModalData] = useState([]);
  const [candidatesModalLoading, setCandidatesModalLoading] = useState(false);
  const [candidatesModalError, setCandidatesModalError] = useState("");
  const [candidatesModalPurchaseId, setCandidatesModalPurchaseId] = useState(null);
  const [candidatesModalPage, setCandidatesModalPage] = useState(1);
  const [candidatesModalPageSize, setCandidatesModalPageSize] = useState(20);
  const [candidatesModalPagination, setCandidatesModalPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });

  // ============ SAMPLE DATA ============

  // Summary Metrics Data
  const staticSummaryMetrics = useMemo(
    () => [
      {
        label: "Total Employers (Purchased)",
        value: 247,
        trend: "+18%",
        trendUp: true,
        sourceType: "static",
      },
      {
        label: "Total Candidate Data Purchased",
        value: 3842,
        trend: "+24%",
        trendUp: true,
        sourceType: "static",
      },
      {
        label: "Total Searches",
        value: 15678,
        trend: "+31%",
        trendUp: true,
        sourceType: "static",
      },
      {
        label: "Total Abandoned Sessions",
        value: 89,
        trend: "-12%",
        trendUp: false,
        sourceType: "static",
      },
      {
        label: "Revenue (Monthly)",
        value: "₹18.4L",
        trend: "+22%",
        trendUp: true,
        sourceType: "static",
      },
      {
        label: "Revenue (Weekly)",
        value: "₹4.2L",
        trend: "+15%",
        trendUp: true,
        sourceType: "static",
      },
    ],
    []
  );

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchSummaryMetrics = async (fromDate, toDate) => {
    try {
      setSummaryLoading(true);
      const isTesterParam = isTesterFilter ? `&isTester=true` : `&isTester=false`;
      const response = await getRequestWithToken(
        `/snf-admin/summary?startDate=${fromDate}&endDate=${toDate}${isTesterParam}`
      );
      setApiSummaryData(response?.data?.data || null);
      setSummaryError("");
    } catch (error) {
      setSummaryError(error?.response?.data?.msg || "Unable to load summary metrics from API.");
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    const today = formatDate(new Date());
    setEndDate(today);
    setEmployersEndDate(today);
    setEmployersEndDateInput(today);
    setCitySearchesEndDate(today);
    setCitySearchesEndDateInput(today);
    setPurchasedEndDate(today);
    setPurchasedEndDateInput(today);
    setAbandonedCartEndDate(today);
    setAbandonedCartEndDateInput(today);
    fetchSummaryMetrics(startDate, today);
    fetchRevenueChart(startDate, today, "weekly");
    fetchCategoryChart(startDate, today);
  }, []);

  useEffect(() => {
    if (!endDate) return;
    fetchSummaryMetrics(startDate, endDate);
    fetchRevenueChart(startDate, endDate, revenuePeriod || "weekly");
    fetchCategoryChart(startDate, endDate);
  }, [isTesterFilter]);

  const fetchEmployers = async (params) => {
    try {
      setEmployersLoading(true);
      setEmployersError("");
      const { page, limit, search, sortBy, order, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (search) query.set("search", search);
      if (sortBy) query.set("sortBy", sortBy);
      if (order) query.set("order", order);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const response = await getRequestWithToken(`/snf-admin/employers?${query.toString()}`);
      const resData = response?.data;
      setEmployersData(resData?.data || []);
      console.table("Fetched employers data:", resData);
      setEmployersPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      setEmployersError(error?.response?.data?.msg || "Unable to load employers data.");
    } finally {
      setEmployersLoading(false);
    }
  };

  useEffect(() => {
    if (!employersEndDate) return;
    fetchEmployers({
      page: employersPage,
      limit: employersPageSize,
      search: employersSearch,
      sortBy: employersSortBy,
      order: employersOrder,
      from: employersStartDate,
      to: employersEndDate,
    });
  }, [employersPage, employersPageSize, employersSearch, employersSortBy, employersOrder, employersStartDate, employersEndDate, isTesterFilter]);

  const fetchCitySearches = async (params) => {
    try {
      setCitySearchesLoading(true);
      setCitySearchesError("");
      const { page, limit, city, state, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (city) query.set("city", city);
      if (state) query.set("state", state);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const response = await getRequestWithToken(`/snf-admin/city-searches?${query.toString()}`);
      const resData = response?.data;
      setCitySearchesData(resData?.data || []);
      setCitySearchesPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      setCitySearchesError(error?.response?.data?.msg || "Unable to load city searches data.");
    } finally {
      setCitySearchesLoading(false);
    }
  };

  useEffect(() => {
    if (!citySearchesEndDate) return;
    fetchCitySearches({
      page: citySearchesPage,
      limit: citySearchesPageSize,
      city: citySearchesCityFilter,
      state: citySearchesStateFilter,
      from: citySearchesStartDate,
      to: citySearchesEndDate,
    });
  }, [citySearchesPage, citySearchesPageSize, citySearchesCityFilter, citySearchesStateFilter, citySearchesStartDate, citySearchesEndDate, isTesterFilter]);

  const fetchPurchasedData = async (params) => {
    try {
      setPurchasedLoading(true);
      setPurchasedError("");
      const { page, limit, city, from, to, demandFulfilled } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (city) query.set("city", city);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      if (demandFulfilled) query.set("demandFulfilled", demandFulfilled);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const response = await getRequestWithToken(`/snf-admin/purchases?${query.toString()}`);
      const resData = response?.data;
      setPurchasedData(resData?.data || []);
      setPurchasedPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      setPurchasedError(error?.response?.data?.msg || "Unable to load purchases data.");
    } finally {
      setPurchasedLoading(false);
    }
  };

  useEffect(() => {
    if (!purchasedEndDate) return;
    fetchPurchasedData({
      page: purchasedPage,
      limit: purchasedPageSize,
      city: purchasedCityFilter,
      from: purchasedStartDate,
      to: purchasedEndDate,
      demandFulfilled: purchasedDemandFulfilled,
    });
  }, [purchasedPage, purchasedPageSize, purchasedCityFilter, purchasedStartDate, purchasedEndDate, purchasedDemandFulfilled, isTesterFilter]);

  const handlePurchasedFilterApply = () => {
    if (!purchasedStartDateInput || !purchasedEndDateInput) {
      setPurchasedFilterError("Please select both start date and end date.");
      return;
    }
    if (new Date(purchasedStartDateInput) > new Date(purchasedEndDateInput)) {
      setPurchasedFilterError("Start date cannot be after end date.");
      return;
    }
    setPurchasedFilterError("");
    setPurchasedPage(1);
    setPurchasedCityFilter(purchasedCityInput);
    setPurchasedStartDate(purchasedStartDateInput);
    setPurchasedEndDate(purchasedEndDateInput);
  };

  const handleCitySearchesFilterApply = () => {
    if (!citySearchesStartDateInput || !citySearchesEndDateInput) {
      setCitySearchesFilterError("Please select both start date and end date.");
      return;
    }
    if (new Date(citySearchesStartDateInput) > new Date(citySearchesEndDateInput)) {
      setCitySearchesFilterError("Start date cannot be after end date.");
      return;
    }
    setCitySearchesFilterError("");
    setCitySearchesPage(1);
    setCitySearchesCityFilter(citySearchesCityInput);
    setCitySearchesStateFilter(citySearchesStateInput);
    setCitySearchesStartDate(citySearchesStartDateInput);
    setCitySearchesEndDate(citySearchesEndDateInput);
  };

  const handleEmployersFilterApply = () => {
    if (!employersStartDateInput || !employersEndDateInput) {
      setEmployersFilterError("Please select both start date and end date.");
      return;
    }
    if (new Date(employersStartDateInput) > new Date(employersEndDateInput)) {
      setEmployersFilterError("Start date cannot be after end date.");
      return;
    }
    setEmployersFilterError("");
    setEmployersPage(1);
    setEmployersSearch(employersSearchInput);
    setEmployersStartDate(employersStartDateInput);
    setEmployersEndDate(employersEndDateInput);
  };

  const fetchAbandonedCarts = async (params) => {
    try {
      setAbandonedCartLoading(true);
      setAbandonedCartError("");
      const { page, limit, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const response = await getRequestWithToken(`/snf-admin/abandoned-carts?${query.toString()}`);
      const resData = response?.data;
      setAbandonedCartApiData(resData?.data || []);
      setAbandonedCartPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      setAbandonedCartError(error?.response?.data?.msg || "Unable to load abandoned cart data.");
    } finally {
      setAbandonedCartLoading(false);
    }
  };

  useEffect(() => {
    if (!abandonedCartEndDate) return;
    fetchAbandonedCarts({
      page: abandonedCartPage,
      limit: abandonedCartPageSize,
      from: abandonedCartStartDate,
      to: abandonedCartEndDate
    });
  }, [abandonedCartPage, abandonedCartPageSize, abandonedCartStartDate, abandonedCartEndDate, isTesterFilter]);

  const updateAbandonedCartStatus = async (sessionId, newStatus) => {
    try {
      setStatusUpdateLoading(true);
      await patchRequestWithToken(`/snf-admin/abandoned-carts/${sessionId}/status`, { adminCartStatus: newStatus });
      setAbandonedCartStatuses((prev) => ({ ...prev, [sessionId]: newStatus }));
    } catch (error) {
      setAbandonedCartError(error?.response?.data?.msg || "Failed to update status.");
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleAbandonedCartFilterApply = () => {
    if (!abandonedCartStartDateInput || !abandonedCartEndDateInput) {
      setAbandonedCartFilterError("Please select both start date and end date.");
      return;
    }
    if (new Date(abandonedCartStartDateInput) > new Date(abandonedCartEndDateInput)) {
      setAbandonedCartFilterError("Start date cannot be after end date.");
      return;
    }
    setAbandonedCartFilterError("");
    setAbandonedCartPage(1);
    setAbandonedCartStartDate(abandonedCartStartDateInput);
    setAbandonedCartEndDate(abandonedCartEndDateInput);
  };

  const fetchPurchaseCandidates = async (purchaseId, page = 1, limit = 20) => {
    try {
      setCandidatesModalLoading(true);
      setCandidatesModalError("");
      setCandidatesModalData([]);
      setCandidatesModalOpen(true);
      setCandidatesModalPurchaseId(purchaseId);
      const response = await getRequestWithToken(`/snf-admin/purchases/${purchaseId}/candidates?page=${page}&limit=${limit}`);
      setCandidatesModalData(response?.data?.data || []);
      setCandidatesModalPagination(response?.data?.pagination || { total: 0, page: 1, limit, pages: 1 });
    } catch (error) {
      setCandidatesModalError(error?.response?.data?.msg || "Unable to load candidate data.");
    } finally {
      setCandidatesModalLoading(false);
    }
  };

  const handleSummaryFilterApply = () => {
    if (!startDate || !endDate) {
      setSummaryError("Please select both start date and end date.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setSummaryError("Start date cannot be after end date.");
      return;
    }
    setRevenuePeriod("");
    fetchSummaryMetrics(startDate, endDate);
    fetchRevenueChart(startDate, endDate, revenuePeriod);
    fetchCategoryChart(startDate, endDate);
  };

  const handleSummaryReset = () => {
    const today = formatDate(new Date());
    const defaultStart = "2026-01-01";
    setStartDate(defaultStart);
    setEndDate(today);
    setSummaryError("");
    setRevenuePeriod("");
    fetchSummaryMetrics(defaultStart, today);
    fetchRevenueChart(defaultStart, today, "weekly");
    fetchCategoryChart(defaultStart, today);
  };

  const handleEmployersReset = () => {
    const today = formatDate(new Date());
    const defaultStart = "2026-01-01";
    setEmployersSearchInput("");
    setEmployersSearch("");
    setEmployersSortBy("");
    setEmployersOrder("");
    setEmployersStartDateInput(defaultStart);
    setEmployersEndDateInput(today);
    setEmployersStartDate(defaultStart);
    setEmployersEndDate(today);
    setEmployersPage(1);
    setEmployersFilterError("");
  };

  const handleCitySearchesReset = () => {
    const today = formatDate(new Date());
    const defaultStart = "2026-01-01";
    setCitySearchesCityInput("");
    setCitySearchesStateInput("");
    setCitySearchesCityFilter("");
    setCitySearchesStateFilter("");
    setCitySearchesStartDateInput(defaultStart);
    setCitySearchesEndDateInput(today);
    setCitySearchesStartDate(defaultStart);
    setCitySearchesEndDate(today);
    setCitySearchesPage(1);
    setCitySearchesFilterError("");
  };

  const handlePurchasedReset = () => {
    const today = formatDate(new Date());
    const defaultStart = "2026-01-01";
    setPurchasedCityInput("");
    setPurchasedCityFilter("");
    setPurchasedDemandFulfilled(null);
    setPurchasedStartDateInput(defaultStart);
    setPurchasedEndDateInput(today);
    setPurchasedStartDate(defaultStart);
    setPurchasedEndDate(today);
    setPurchasedPage(1);
    setPurchasedFilterError("");
  };

  const handleAbandonedCartReset = () => {
    const today = formatDate(new Date());
    const defaultStart = "2026-01-01";
    setAbandonedCartStartDateInput(defaultStart);
    setAbandonedCartEndDateInput(today);
    setAbandonedCartStartDate(defaultStart);
    setAbandonedCartEndDate(today);
    setAbandonedCartPage(1);
    setAbandonedCartFilterError("");
  };

  const summaryMetrics = useMemo(() => {
    if (!apiSummaryData) return [];
    return [
      {
        label: "Purchases & Employers",
        items: [
          { label: "Total Purchases", value: apiSummaryData.totalPurchases ?? "-" },
          { label: "Employers Used SNF", value: apiSummaryData.totalEmployersUsedSNF ?? "-" },
        ],
      },
      {
        label: "Candidates & Sessions",
        items: [
          { label: "Candidates Allocated", value: apiSummaryData.totalCandidatesAllocated ?? "-" },
          { label: "Total Sessions", value: apiSummaryData.totalSessions ?? "-" },
        ],
      },
      {
        label: "Abandoned Cart",
        items: [
          { label: "Total Abandoned", value: apiSummaryData.totalAbandonedCarts ?? "-" },
          {
            label: "Cart Rate",
            value: apiSummaryData.abandonedCartRate !== undefined ? `${apiSummaryData.abandonedCartRate}%` : "-",
          },
        ],
      },
      {
        label: "Total Spent",
        split: true,
        items: [
          {
            label: "PHC Spent",
            value: apiSummaryData.totalPhcSpent !== undefined ? Number(apiSummaryData.totalPhcSpent).toLocaleString() : "-",
          },
          {
            label: "Total Revenue",
            value: apiSummaryData.totalRevenue !== undefined ? `₹${Number(apiSummaryData.totalRevenue).toLocaleString()}` : "-",
          },
        ],
      },
    ];
  }, [apiSummaryData]);

  // Revenue Chart Data
  const fetchRevenueChart = async (fromDate, toDate, period) => {
    try {
      setRevenueChartLoading(true);
      setRevenueChartError("");
      const query = new URLSearchParams();
      if (!period) {

        if (fromDate) query.set("startDate", fromDate);
        if (toDate) query.set("endDate", toDate);
      }
      if (period) query.set("filter", period);
      if (period === "month") query.set("months", "6");
      query.set("isTester", isTesterFilter ? "true" : "false");
      const response = await getRequestWithToken(`/snf-admin/graphs/weekly-revenue?${query.toString()}`);
      setRevenueChartData(response?.data?.data || []);
    } catch (error) {
      setRevenueChartError(error?.response?.data?.msg || "Unable to load revenue chart.");
    } finally {
      setRevenueChartLoading(false);
    }
  };

  // Category Distribution Data for Pie Chart
  const fetchCategoryChart = async (fromDate, toDate) => {
    try {
      setCategoryChartLoading(true);
      setCategoryChartError("");
      const query = new URLSearchParams();
      if (fromDate) query.set("startDate", fromDate);
      if (toDate) query.set("endDate", toDate);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const response = await getRequestWithToken(`/snf-admin/graphs/category-distribution?${query.toString()}`);
      setCategoryChartData(response?.data?.data || []);
    } catch (error) {
      setCategoryChartError(error?.response?.data?.msg || "Unable to load category chart.");
    } finally {
      setCategoryChartLoading(false);
    }
  };



  // Abandoned Cart Data (Tab 4)
  // ============ TAB HANDLERS ============

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchParams({ tab: newValue }, { replace: true });
  };

  const handleStatusMenuOpen = (event, row) => {
    setStatusMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setSelectedRow(null);
  };

  const handleStatusChange = (newStatus) => {
    if (selectedRow) {
      const sessionId = selectedRow.original.sessionId;
      updateAbandonedCartStatus(sessionId, newStatus);
    }
    handleStatusMenuClose();
  };

  const getRowStatus = (row) => {
    return abandonedCartStatuses[row.sessionId] || row.adminCartStatus || "Pending";
  };

  // ============ EMPLOYERS TABLE (Tab 1) ============

  const employersColumns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "S.No.",
        size: 120,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <span>{row.index + 1 + (employersPage - 1) * employersPageSize}</span>
        ),
      },
      {
        accessorKey: "employerName",
        header: "Company",
        size: 160,
        maxSize: 160,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, py: 0.5, maxWidth: 160 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>
              {row.original.employerName || "-"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.contactNumber || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "city",
        header: "Location",
        size: 120,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            <Typography variant="body2">{row.original.city || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">{row.original.state || "-"}</Typography>
          </Box>
        ),
      },
      {
        accessorKey: "totalSearches",
        header: "Total Searches",
        size: 140,
      },
      {
        accessorKey: "candidateDemand",
        header: "Candidates",
        size: 150,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            <Typography variant="body2">Demand: {row.original.candidateDemand ?? "-"}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>Provided: {row.original.candidateDataProvided ?? "-"}</Typography>
          </Box>
        ),
      },
      {
        accessorKey: "urgencyLevel",
        header: "Urgency / Category",
        size: 190,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>{row.original.urgencyLevel || "-"}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>{row.original.mostSearchedCategory || "-"}</Typography>
          </Box>
        ),
      },
      {
        accessorKey: "lastSessionAt",
        header: "Last Session",
        size: 160,
        Cell: ({ cell }) => {
          const val = cell.getValue();
          if (!val) return "-";
          return new Date(val).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 100,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <IconButton
            color="primary"
            onClick={() => window.open(`/employer/detail/${row.original.employerId}`, "_blank", "noopener,noreferrer")}
            title="View Details"
          >
            <VisibilityIcon />
          </IconButton>
        ),
      },
    ],
    [navigate, employersPage, employersPageSize]
  );

  const employersTable = useMaterialReactTable({
    columns: employersColumns,
    data: employersData,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    layoutMode: "grid",
    state: { isLoading: employersLoading },
    initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  // ============ MOST SEARCHES TABLE (Tab 2) ============

  const mostSearchesColumns = useMemo(
    () => [
      {
        accessorKey: "rank",
        header: "Rank",
        size: 70,
      },
      {
        accessorKey: "category",
        header: "Category / Role",
        size: 200,
      },
      // {
      //   accessorKey: "city",
      //   header: "City",
      //   size: 140,
      //   Cell: ({ cell }) => cell.getValue() || "-",
      // },
      {
        accessorKey: "searchCount",
        header: "Search Count",
        size: 140,
      },
    ],
    []
  );

  const mostSearchesTable = useMaterialReactTable({
    columns: mostSearchesColumns,
    data: citySearchesData,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    layoutMode: "grid",
    state: { isLoading: citySearchesLoading },
    initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  // ============ PURCHASED DATA TABLE (Tab 3) ============

  const purchasedDataColumns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "S.No.",
        size: 120,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <span>{row.index + 1 + (purchasedPage - 1) * purchasedPageSize}</span>
        ),
      },
      {
        accessorKey: "employerName",
        header: "Employer",
        size: 250,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, py: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>
              {row.original.employerName || "-"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.contactNumber || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "address",
        header: "Location",
        size: 220,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "normal" }}>
            {cell.getValue() || "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "candidateDemand",
        header: "Demand / Provided",
        size: 140,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            <Typography variant="body2">Demand: {row.original.candidateDemand ?? "-"}</Typography>
            <Typography variant="caption" color="text.secondary">Provided: {row.original.dataProvided ?? "-"}</Typography>
          </Box>
        ),
      },
      {
        accessorKey: "costOfPurchase",
        header: "Total Cost",
        size: 110,
        Cell: ({ cell }) => Number(cell.getValue()).toLocaleString(),
      },
      {
        accessorKey: "purchaseDate",
        header: "Purchase Date",
        size: 160,
        Cell: ({ cell }) => {
          const val = cell.getValue();
          if (!val) return "-";
          return new Date(val).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        },
      },
      {
        accessorKey: "demandFulfilled",
        header: "Fulfillment",
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "success.main" }}>
              <TrendingUpIcon fontSize="small" />
              <Typography variant="body2">Fulfilled</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "error.main" }}>
              <TrendingDownIcon fontSize="small" />
              <Typography variant="body2">Partial</Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 100,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <IconButton
            color="primary"
            onClick={() => fetchPurchaseCandidates(row.original.purchaseId)}
            title="View Purchased Candidates"
          >
            <VisibilityIcon />
          </IconButton>
        ),
      },
    ],
    [purchasedPage, purchasedPageSize]
  );

  const purchasedDataTable = useMaterialReactTable({
    columns: purchasedDataColumns,
    data: purchasedData,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    layoutMode: "grid",
    state: { isLoading: purchasedLoading },
    initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  // ============ ABANDONED CART TABLE (Tab 4) ============

  const abandonedCartColumns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "S.No.",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <span>{row.index + 1 + (abandonedCartPage - 1) * abandonedCartPageSize}</span>
        ),
      },
      {
        accessorKey: "employerName",
        header: "Employer",
        size: 260,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, py: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>
              {row.original.employerName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "normal" }}>
              {row.original.employerPhone}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", whiteSpace: "normal" }}>
              {row.original.employerEmail}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "address",
        header: "Location",
        size: 220,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "normal" }}>
            {cell.getValue() || "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "numberOfSessions",
        header: "Sessions",
        size: 120,
      },
      {
        accessorKey: "cartValue",
        header: "Cart Value",
        size: 120,
      },
      {
        accessorKey: "dateTime",
        header: "Date & Time",
        size: 180,
        Cell: ({ cell }) => {
          const val = cell.getValue();
          if (!val) return "-";
          return new Date(val).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
      },


      // {
      //   accessorKey: "searchesCity",
      //   header: "City",
      //   size: 120,
      //   Cell: ({ cell }) => cell.getValue() || "-",
      // },
      {
        accessorKey: "adminCartStatus",
        header: "Status",
        size: 150,
        Cell: ({ row }) => {
          const value = getRowStatus(row.original);
          const styleMap = {
            "Pending": { backgroundColor: "rgba(255,152,0,0.12)", color: "#bf6000" },
            "Contacted": { backgroundColor: "rgba(33,150,243,0.12)", color: "#0a5c99" },
            "Follow-up Required": { backgroundColor: "rgba(244,67,54,0.12)", color: "#b71c1c" },
            "Converted": { backgroundColor: "rgba(76,175,80,0.12)", color: "#2e7d32" },
          };
          return (
            <Chip
              label={value}
              size="small"
              sx={styleMap[value] || { backgroundColor: "rgba(0,0,0,0.06)", color: "text.secondary" }}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            disabled={statusUpdateLoading}
            onClick={(e) => handleStatusMenuOpen(e, row)}
          >
            Update
          </Button>
        ),
      },
    ],
    [abandonedCartStatuses, getRowStatus, handleStatusMenuOpen, statusUpdateLoading, abandonedCartPage, abandonedCartPageSize]
  );

  const abandonedCartTable = useMaterialReactTable({
    columns: abandonedCartColumns,
    data: abandonedCartApiData,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    layoutMode: "grid",
    state: { isLoading: abandonedCartLoading },
    initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  // ============ RENDER ============

  return (
    <PageContainer
      title="SNF Dashboard"
      subtitle="Smart Nearby Feature - Monitor candidate purchase data for employers and recruiters"
    >

      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", pb: 1, position: "absolute", right: '50px' }}>
        <FormControlLabel
          control={
            <Switch
              checked={isTesterFilter}
              onChange={(e) => setIsTesterFilter(e.target.checked)}
              color="warning"
            />
          }
          label="Test Data"
        />
      </Box>
      <div className="snf-dashboard">

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="SNF Dashboard Tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Dashboard / Metrics" {...a11yProps(0)} />
            <Tab label="Employers" {...a11yProps(1)} />
            <Tab label="Most Searches" {...a11yProps(2)} />
            <Tab label="Purchased Data" {...a11yProps(3)} />
            <Tab label="Abandoned Cart" {...a11yProps(4)} />

          </Tabs>

        </Box>

        {/* TAB 0: Dashboard / Metrics */}
        <CustomTabPanel value={tabValue} index={0}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            sx={{ mb: 2 }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={handleSummaryFilterApply}
              disabled={summaryLoading}
            >
              {summaryLoading ? "Applying..." : "Apply"}
            </Button>
            <Button
              variant="outlined"
              // size="small"
              color="inherit"
              onClick={handleSummaryReset}
              disabled={summaryLoading}
            >
              Reset
            </Button>
          </Stack>

          {summaryError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {summaryError}
            </Alert>
          ) : null}
          <Grid container spacing={2}>
            {summaryLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                    <CardContent>
                      <Skeleton variant="text" width="50%" height={18} sx={{ mb: 1.5 }} />
                      <Grid container spacing={1}>
                        {[0, 1].map((i) => (
                          <Grid item xs={6} key={i}>
                            <Box sx={{ p: 1, borderRadius: 1, backgroundColor: "rgba(0,0,0,0.04)" }}>
                              <Skeleton variant="text" width="80%" height={14} />
                              <Skeleton variant="text" width="60%" height={32} sx={{ mt: 0.5 }} />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))
              : summaryMetrics.map((metric, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  {metric.split ? (
                    <Stack spacing={1} sx={{ height: "100%" }}>
                      {metric.items.map((item, i) => (
                        <Card
                          key={i}
                          className="snf-metric-card"
                          sx={{
                            boxShadow: "var(--jy-card-shadow)",
                            border: "1px solid var(--jy-border)",
                            backgroundColor: "#fff",
                            flex: 1,
                          }}
                        >
                          <CardContent sx={{ py: "12px !important" }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: 0.8,
                                color: "primary.main",
                                display: "block",
                                mb: 0.75,
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                              {typeof item.value === "number" ? item.value.toLocaleString() : item.value}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Card
                      className="snf-metric-card"
                      sx={{
                        boxShadow: "var(--jy-card-shadow)",
                        border: "1px solid var(--jy-border)",
                        backgroundColor: "#fff",
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 0.8,
                            color: "primary.main",
                            display: "block",
                            mb: 1.5,
                          }}
                        >
                          {metric.label}
                        </Typography>
                        <Grid container spacing={1}>
                          {metric.items.map((item, i) => (
                            <Grid item xs={6} key={i}>
                              <Box
                                sx={{
                                  p: 1,
                                  borderRadius: 1,
                                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                                  border: "1px solid rgba(25, 118, 210, 0.08)",
                                  minHeight: 72,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>
                                  {item.label}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.2 }}>
                                  {typeof item.value === "number" ? item.value.toLocaleString() : item.value}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  )}
                </Grid>
              ))}
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Revenue Trend</Typography>
                      <Typography variant="caption" color="text.secondary">PHC Spent (bars) &amp; Purchase Count (line)</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        variant={revenuePeriod === "today" ? "contained" : "outlined"}
                        onClick={() => {
                          setRevenuePeriod("today");
                          fetchRevenueChart(startDate, endDate, "today");
                        }}
                        disabled={revenueChartLoading}
                      >
                        Today
                      </Button>
                      <Button
                        size="small"
                        variant={revenuePeriod === "yesterday" ? "contained" : "outlined"}
                        onClick={() => {
                          setRevenuePeriod("yesterday");
                          fetchRevenueChart(startDate, endDate, "yesterday");
                        }}
                        disabled={revenueChartLoading}
                      >
                        Yesterday
                      </Button>
                      <Button
                        size="small"
                        variant={revenuePeriod === "month" ? "contained" : "outlined"}
                        onClick={() => {
                          setRevenuePeriod("month");
                          fetchRevenueChart(startDate, endDate, "month");
                        }}
                        disabled={revenueChartLoading}
                      >
                        Month
                      </Button>
                      <Button
                        size="small"
                        variant={revenuePeriod === "Week" ? "contained" : "outlined"}
                        onClick={() => {
                          setRevenuePeriod("Week");
                          fetchRevenueChart(startDate, endDate, "Week");
                        }}
                        disabled={revenueChartLoading}
                      >
                        Weekly
                      </Button>
                    </Stack>
                  </Box>
                  {revenueChartError ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>{revenueChartError}</Alert>
                  ) : null}
                  {revenueChartLoading ? (
                    <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <ComposedChart data={revenueChartData} margin={{ top: 10, right: 40, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis
                          yAxisId="left"
                          orientation="left"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                          label={{ value: "PHC", angle: -90, position: "insideLeft", offset: 10, fontSize: 11 }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fontSize: 11 }}
                          label={{ value: "Purchases", angle: 90, position: "insideRight", offset: 10, fontSize: 11 }}
                        />
                        <RechartsTooltip
                          formatter={(value, name) =>
                            name === "Revenue (PHC)"
                              ? [`${Number(value).toLocaleString()} PHC`, name]
                              : [value, name]
                          }
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar yAxisId="left" dataKey="count" name="Revenue (PHC)" fill="#1976d2" radius={[3, 3, 0, 0]} />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="purchaseCount"
                          name="Purchases"
                          stroke="#ff9800"
                          strokeWidth={2}
                          dot={{ r: 4, fill: "#ff9800" }}
                          activeDot={{ r: 6 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Roles Distribution</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Candidate data by roles</Typography>
                  {categoryChartError ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>{categoryChartError}</Alert>
                  ) : null}
                  {categoryChartLoading ? (
                    <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(240, categoryChartData.length * 72)}>
                      <BarChart
                        data={categoryChartData}
                        layout="vertical"
                        margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                        <YAxis
                          dataKey="label"
                          type="category"
                          width={200}
                          tick={<CustomYAxisTick />}
                          interval={0}
                        />
                        <RechartsTooltip content={<CategoryTooltip />} />
                        <Bar dataKey="value" name="Candidates" radius={[0, 3, 3, 0]} label={{ position: "right", fontSize: 11, formatter: (v) => Number(v).toLocaleString() }}>
                          {categoryChartData.map((_, i) => (
                            <Cell key={i} fill={["#1976d2", "#42a5f5", "#66bb6a", "#ff9800", "#ef5350", "#ab47bc", "#26c6da"][i % 7]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CustomTabPanel>

        {/* TAB 1: Employers Table */}
        <CustomTabPanel value={tabValue} index={1}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <TextField
              label="Search Company"
              size="small"
              placeholder="e.g. Acme"
              value={employersSearchInput}
              onChange={(e) => setEmployersSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleEmployersFilterApply(); }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={employersStartDateInput}
              onChange={(e) => setEmployersStartDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={employersEndDateInput}
              onChange={(e) => setEmployersEndDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Sort By"
              size="small"
              select
              value={employersSortBy}
              onChange={(e) => { setEmployersSortBy(e.target.value); setEmployersPage(1); }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="totalSessions">Total Searches</MenuItem>
              {/* <MenuItem value="totalPurchases">Total Purchases</MenuItem>
              <MenuItem value="abandonedSessions">Abandoned Sessions</MenuItem> */}
              <MenuItem value="lastSessionAt">Last Session</MenuItem>
            </TextField>
            <TextField
              label="Order"
              size="small"
              select
              value={employersOrder}
              onChange={(e) => { setEmployersOrder(e.target.value); setEmployersPage(1); }}
              sx={{ minWidth: 160 }}
            >
              {/* <MenuItem value="">None</MenuItem> */}
              <MenuItem value="desc">Descending</MenuItem>
              <MenuItem value="asc">Ascending</MenuItem>
            </TextField>
            <Button
              variant="contained"
              onClick={handleEmployersFilterApply}
              disabled={employersLoading}
            >
              {employersLoading ? "Loading..." : "Apply"}
            </Button>
            <Button
              variant="outlined"
              // size="small"
              color="inherit"
              onClick={handleEmployersReset}
              disabled={employersLoading}
            >
              Reset
            </Button>
          </Stack>

          {employersFilterError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>{employersFilterError}</Alert>
          ) : null}
          {employersError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{employersError}</Alert>
          ) : null}

          <DataTable table={employersTable} />

          {/* Pagination controls */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {employersPagination.total > 0
                ? `Showing page ${employersPagination.page} of ${employersPagination.pages} (${employersPagination.total} total)`
                : "No results"}
            </Typography>
            <TextField
              label="Rows"
              size="small"
              select
              value={employersPageSize}
              onChange={(e) => { setEmployersPageSize(Number(e.target.value)); setEmployersPage(1); }}
              sx={{ width: 80 }}
            >
              {[10, 20, 50].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              variant="outlined"
              disabled={employersPage <= 1 || employersLoading}
              onClick={() => setEmployersPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={employersPage >= employersPagination.pages || employersLoading}
              onClick={() => setEmployersPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </CustomTabPanel>

        {/* TAB 2: Most Searches (City-wise) */}
        <CustomTabPanel value={tabValue} index={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            {/* <TextField
              label="City"
              size="small"
              placeholder="e.g. Mumbai"
              value={citySearchesCityInput}
              onChange={(e) => setCitySearchesCityInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleCitySearchesFilterApply(); }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              label="State"
              size="small"
              placeholder="e.g. Maharashtra"
              value={citySearchesStateInput}
              onChange={(e) => setCitySearchesStateInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleCitySearchesFilterApply(); }}
              sx={{ minWidth: 160 }}
            /> */}
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={citySearchesStartDateInput}
              onChange={(e) => setCitySearchesStartDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={citySearchesEndDateInput}
              onChange={(e) => setCitySearchesEndDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={handleCitySearchesFilterApply}
              disabled={citySearchesLoading}
            >
              {citySearchesLoading ? "Loading..." : "Apply"}
            </Button>
            <Button
              variant="outlined"
              // size="small"
              color="inherit"
              onClick={handleCitySearchesReset}
              disabled={citySearchesLoading}
            >
              Reset
            </Button>
          </Stack>

          {citySearchesFilterError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>{citySearchesFilterError}</Alert>
          ) : null}
          {citySearchesError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{citySearchesError}</Alert>
          ) : null}

          <DataTable table={mostSearchesTable} />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {citySearchesPagination.total > 0
                ? `Showing page ${citySearchesPagination.page} of ${citySearchesPagination.pages} (${citySearchesPagination.total} total)`
                : "No results"}
            </Typography>
            <TextField
              label="Rows"
              size="small"
              select
              value={citySearchesPageSize}
              onChange={(e) => { setCitySearchesPageSize(Number(e.target.value)); setCitySearchesPage(1); }}
              sx={{ width: 80 }}
            >
              {[10, 20, 50].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              variant="outlined"
              disabled={citySearchesPage <= 1 || citySearchesLoading}
              onClick={() => setCitySearchesPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={citySearchesPage >= citySearchesPagination.pages || citySearchesLoading}
              onClick={() => setCitySearchesPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </CustomTabPanel>

        {/* TAB 3: Purchased Data */}
        <CustomTabPanel value={tabValue} index={3}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            sx={{ mb: 2 }}
          >
            {/* <TextField
              label="City"
              size="small"
              placeholder="e.g. Mumbai"
              value={purchasedCityInput}
              onChange={(e) => setPurchasedCityInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handlePurchasedFilterApply(); }}
              sx={{ minWidth: 150 }}
            /> */}
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={purchasedStartDateInput}
              onChange={(e) => setPurchasedStartDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={purchasedEndDateInput}
              onChange={(e) => setPurchasedEndDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Sort By"
              size="small"
              select
              value={purchasedDemandFulfilled}
              onChange={(e) => { setPurchasedDemandFulfilled(e.target.value); setAbandonedCartPage(1); }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Fulfilled</MenuItem>
              <MenuItem value="false">Partial</MenuItem>
            </TextField>
            <Button
              variant="contained"
              onClick={handlePurchasedFilterApply}
              disabled={purchasedLoading}
            >
              {purchasedLoading ? "Loading..." : "Apply"}
            </Button>
            <Button
              variant="outlined"
              // size="small"
              color="inherit"
              onClick={handlePurchasedReset}
              disabled={purchasedLoading}
            >
              Reset
            </Button>
          </Stack>

          {purchasedFilterError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>{purchasedFilterError}</Alert>
          ) : null}
          {purchasedError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{purchasedError}</Alert>
          ) : null}

          <DataTable table={purchasedDataTable} />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {purchasedPagination.total > 0
                ? `Showing page ${purchasedPagination.page} of ${purchasedPagination.pages} (${purchasedPagination.total} total)`
                : "No results"}
            </Typography>
            <TextField
              label="Rows"
              size="small"
              select
              value={purchasedPageSize}
              onChange={(e) => { setPurchasedPageSize(Number(e.target.value)); setPurchasedPage(1); }}
              sx={{ width: 80 }}
            >
              {[10, 20, 50].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              variant="outlined"
              disabled={purchasedPage <= 1 || purchasedLoading}
              onClick={() => setPurchasedPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={purchasedPage >= purchasedPagination.pages || purchasedLoading}
              onClick={() => setPurchasedPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </CustomTabPanel>

        {/* TAB 4: Abandoned Cart */}
        <CustomTabPanel value={tabValue} index={4}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            sx={{ mb: 2 }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={abandonedCartStartDateInput}
              onChange={(e) => setAbandonedCartStartDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={abandonedCartEndDateInput}
              onChange={(e) => setAbandonedCartEndDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <Button
              variant="contained"
              onClick={handleAbandonedCartFilterApply}
              disabled={abandonedCartLoading}
            >
              {abandonedCartLoading ? "Loading..." : "Apply"}
            </Button>
            <Button
              variant="outlined"
              // size="small"
              color="inherit"
              onClick={handleAbandonedCartReset}
              disabled={abandonedCartLoading}
            >
              Reset
            </Button>
          </Stack>

          {abandonedCartFilterError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>{abandonedCartFilterError}</Alert>
          ) : null}
          {abandonedCartError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{abandonedCartError}</Alert>
          ) : null}

          <DataTable table={abandonedCartTable} />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {abandonedCartPagination.total > 0
                ? `Showing page ${abandonedCartPagination.page} of ${abandonedCartPagination.pages} (${abandonedCartPagination.total} total)`
                : "No results"}
            </Typography>
            <TextField
              label="Rows"
              size="small"
              select
              value={abandonedCartPageSize}
              onChange={(e) => { setAbandonedCartPageSize(Number(e.target.value)); setAbandonedCartPage(1); }}
              sx={{ width: 80 }}
            >
              {[10, 20, 50].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              variant="outlined"
              disabled={abandonedCartPage <= 1 || abandonedCartLoading}
              onClick={() => setAbandonedCartPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={abandonedCartPage >= abandonedCartPagination.pages || abandonedCartLoading}
              onClick={() => setAbandonedCartPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>

          {/* Status Update Menu */}
          <Menu
            anchorEl={statusMenuAnchor}
            open={Boolean(statusMenuAnchor)}
            onClose={handleStatusMenuClose}
          >
            <MenuItem onClick={() => handleStatusChange("Pending")}>Pending</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Contacted")}>Contacted</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Follow-up Required")}>Follow-up Required</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Converted")}>Converted</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Not Answered")}>Not Answered'</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Not Interested")}>Not Interested</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Callback Requested")}>Callback Requested</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Invalid Contact")}>Invalid Contact</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Disqualified")}>Disqualified</MenuItem>
          </Menu>
        </CustomTabPanel>
      </div>

      {/* Purchased Candidates Modal */}
      <ModalComponent
        openModal={candidatesModalOpen}
        setOpenModal={setCandidatesModalOpen}
        modalTitle="Purchased Candidates"
        handleOnCloseBtnClick={() => {
          setCandidatesModalOpen(false);
          setCandidatesModalData([]);
          setCandidatesModalError("");
          setCandidatesModalPurchaseId(null);
          setCandidatesModalPage(1);
          setCandidatesModalPagination({ total: 0, page: 1, limit: 20, pages: 1 });
        }}
        modalWidth="70%"
      >
        <Box sx={{ p: 2 }}>
          {candidatesModalError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{candidatesModalError}</Alert>
          ) : null}
          {candidatesModalLoading ? (
            <Stack spacing={1}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
              ))}
            </Stack>
          ) : candidatesModalData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No candidate data found for this purchase.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e0e0e0", textAlign: "left" }}>
                    <th style={{ padding: "8px 12px" }}>#</th>
                    <th style={{ padding: "8px 12px" }}>Name</th>
                    <th style={{ padding: "8px 12px" }}>Contact</th>
                    <th style={{ padding: "8px 12px" }}>Location</th>
                    <th style={{ padding: "8px 12px" }}>Status</th>
                    <th style={{ padding: "8px 12px" }}>Distance</th>
                  </tr>
                </thead>
                <tbody>
                  {candidatesModalData.map((c, i) => (
                    <tr key={c.candidateId || i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "8px 12px" }}>{i + 1 + (candidatesModalPage - 1) * candidatesModalPageSize}</td>
                      <td style={{ padding: "8px 12px", fontWeight: 500 }}>{c.name || "-"}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <div>{c.mobile || "-"}</div>
                        {c.email ? <div style={{ fontSize: 12, color: "#888" }}>{c.email}</div> : null}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        {[c.address, c.city, c.state].filter(Boolean).join(", ") || "-"}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <Chip label={c.activityStatus || "fresh"} size="small" />
                      </td>
                      <td style={{ padding: "8px 12px" }}>{c.distanceBucket || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
          {/* Pagination controls */}
          {candidatesModalPagination.total > 0 && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
              sx={{ mt: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing page {candidatesModalPagination.page} of {candidatesModalPagination.pages} ({candidatesModalPagination.total} total)
              </Typography>
              <TextField
                label="Rows"
                size="small"
                select
                value={candidatesModalPageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  setCandidatesModalPageSize(newSize);
                  setCandidatesModalPage(1);
                  fetchPurchaseCandidates(candidatesModalPurchaseId, 1, newSize);
                }}
                sx={{ width: 80 }}
              >
                {[10, 20, 50].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </TextField>
              <Button
                size="small"
                variant="outlined"
                disabled={candidatesModalPage <= 1 || candidatesModalLoading}
                onClick={() => {
                  const newPage = candidatesModalPage - 1;
                  setCandidatesModalPage(newPage);
                  fetchPurchaseCandidates(candidatesModalPurchaseId, newPage, candidatesModalPageSize);
                }}
              >
                Prev
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={candidatesModalPage >= candidatesModalPagination.pages || candidatesModalLoading}
                onClick={() => {
                  const newPage = candidatesModalPage + 1;
                  setCandidatesModalPage(newPage);
                  fetchPurchaseCandidates(candidatesModalPurchaseId, newPage, candidatesModalPageSize);
                }}
              >
                Next
              </Button>
            </Stack>
          )}
        </Box>
      </ModalComponent>
    </PageContainer>
  );
};

export default SnfDashboard;
